import { useCallback, useEffect, useState } from 'react';

import { isSSR } from '@telialt/utils/src/helpers';

type UseDynamicQueryParamKeyOptions = {
    possibleKeys: string[];
    searchParams?: { [key: string]: string | undefined };
    defaultKey?: string;
};

export const useDynamicQueryParamKey = (
    options: UseDynamicQueryParamKeyOptions,
): readonly [string | undefined, (newKey?: string) => void] => {
    const { possibleKeys, searchParams, defaultKey } = options;

    const getKeyFromUrl = () => {
        if (isSSR()) {
            if (!searchParams) {
                return undefined;
            }

            return possibleKeys.find((key) => searchParams[key] === '');
        }

        const urlSearchParams = new URLSearchParams(window.location.search);

        return possibleKeys.find((key) => urlSearchParams.has(key));
    };

    const [activeKey, setActiveKey] = useState<string | undefined>(getKeyFromUrl);

    useEffect(() => {
        const updateKey = () => {
            setActiveKey(getKeyFromUrl());
        };

        updateKey();
        window.addEventListener('popstate', updateKey);

        return () => window.removeEventListener('popstate', updateKey);
    }, [possibleKeys.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

    const setKey = useCallback(
        (newKey?: string) => {
            const url = new URL(window.location.href);

            if (activeKey) {
                url.searchParams.delete(activeKey);
            }

            if (newKey && newKey !== defaultKey) {
                url.searchParams.set(newKey, '');
            }

            const urlString = url.toString().replace(/=(&|$)/g, '$1');
            window.history.pushState(window.history.state, '', urlString);
            window.dispatchEvent(new CustomEvent<string>('popstate'));
        },
        [activeKey, defaultKey],
    );

    return [activeKey, setKey] as const;
};
